import {Box, CircularProgress} from '@mui/material';
import * as Sentry from '@sentry/react';
import {QueryClientProvider} from '@tanstack/react-query';
import {ConsoleLogger, Hub} from 'aws-amplify/utils';
import {SnackbarProvider, enqueueSnackbar} from 'notistack';
// 1. On importe Suspense et lazy
import React, {useEffect, Suspense, lazy} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
// On importe un loader (MUI ou autre) pour l'attente

import {useTrackPageView} from './analytics/useTrackPageView';
import {CrispProvider} from './components/CrispProvider';
import {NavigateKeepingSearch} from './components/NavigateKeepingSearch';
import {sessionKeys} from './hooks/session/keys';
import {SessionProvider} from './hooks/session/SessionProvider';
import {TeamSelectionProvider} from './hooks/team/TeamSelectionProvider';
import {showApiFailureSnackbar} from './hooks/useApiFailureSnackbar';
import {EstablishingConnection} from './components/snackbars/EstablishingConnection';
import {FreeTrial} from './components/snackbars/FreeTrial';
import {LinkedinSessionUnavailable} from './components/snackbars/LinkedinSessionUnavailable';
import {NoLinkedInConnection} from './components/snackbars/NoLinkedInConnection';
import {WorkingCommand} from './components/snackbars/WorkingCommand';
import {i18n} from './i18n';
import {LoginRequiredRoute} from './LoginRequiredRoute';
import {createQueryClient} from './utils/createQueryClient';
import {lazyWithRetry} from './utils/lazyWithRetry';
import {Authentication} from './views/authentication/Authentication';
import {Confirm} from './views/authentication/Confirm';
import {Reset} from './views/authentication/Reset';
import {Signin} from './views/authentication/Signin';
import {Signup} from './views/authentication/Signup';
import {DraftSharing} from './views/DraftSharing'; // Public
import {ExtensionChatKit} from './views/ExtensionChatKit'; // Probablement léger

// --- IMPORTS LAZY (LOURDS) ---
// Le navigateur ne téléchargera ces fichiers QUE si l'utilisateur va sur la route

// Layouts
const DashboardLayout = lazy(() =>
  lazyWithRetry(() => import('./DashboardLayout')).then((m) => ({default: m.DashboardLayout})),
);
const TeamWrapper = lazy(() => lazyWithRetry(() => import('./TeamWrapper')).then((m) => ({default: m.TeamWrapper})));
const OrganizationWrapper = lazy(() =>
  lazyWithRetry(() => import('./views/organization/OrganizationWrapper')).then((m) => ({
    default: m.OrganizationWrapper,
  })),
);

// Vues principales
const Dashboard = lazy(() => lazyWithRetry(() => import('./views/Dashboard')).then((m) => ({default: m.Dashboard})));
const Posts = lazy(() => lazyWithRetry(() => import('./views/Posts')).then((m) => ({default: m.Posts})));
const PostDetail = lazy(() => lazyWithRetry(() => import('./views/PostDetail')).then((m) => ({default: m.PostDetail})));
const Calendar = lazy(() => lazyWithRetry(() => import('./views/Calendar')).then((m) => ({default: m.Calendar})));
const Statistics = lazy(() => lazyWithRetry(() => import('./views/Statistics')).then((m) => ({default: m.Statistics})));
const Monitoring = lazy(() => lazyWithRetry(() => import('./views/Monitoring')).then((m) => ({default: m.Monitoring})));
const Link = lazy(() => lazyWithRetry(() => import('./views/Link')).then((m) => ({default: m.Link})));
const Settings = lazy(() => lazyWithRetry(() => import('./views/Settings')).then((m) => ({default: m.Settings})));
const Integration = lazy(() =>
  lazyWithRetry(() => import('./views/Integration')).then((m) => ({default: m.Integration})),
);
const Referral = lazy(() => lazyWithRetry(() => import('./views/Referral')).then((m) => ({default: m.Referral})));

// Vues Auth Callback (Peuvent être lazy car on y arrive après redirection)
const LinkedInAuth = lazy(() =>
  lazyWithRetry(() => import('./views/LinkedInAuth')).then((m) => ({default: m.LinkedInAuth})),
);
const NotionCallback = lazy(() =>
  lazyWithRetry(() => import('./views/NotionCallback')).then((m) => ({default: m.NotionCallback})),
);
const CanvaCallback = lazy(() =>
  lazyWithRetry(() => import('./views/CanvaCallback')).then((m) => ({default: m.CanvaCallback})),
);
const GoogleCalendarCallback = lazy(() =>
  lazyWithRetry(() => import('./views/GoogleCalendarCallback')).then((m) => ({default: m.GoogleCalendarCallback})),
);
const MicrosoftCalendarCallback = lazy(() =>
  lazyWithRetry(() => import('./views/MicrosoftCalendarCallback')).then((m) => ({
    default: m.MicrosoftCalendarCallback,
  })),
);

// Vues Organization
const OrganizationDashboard = lazy(() =>
  lazyWithRetry(() => import('./views/organization/OrganizationDashboard')).then((m) => ({
    default: m.OrganizationDashboard,
  })),
);
const OrganizationPosts = lazy(() =>
  lazyWithRetry(() => import('./views/organization/OrganizationPosts')).then((m) => ({default: m.OrganizationPosts})),
);
const OrganizationCalendar = lazy(() =>
  lazyWithRetry(() => import('./views/organization/OrganizationCalendar')).then((m) => ({
    default: m.OrganizationCalendar,
  })),
);
const OrganizationStatistics = lazy(() =>
  lazyWithRetry(() => import('./views/organization/OrganizationStatistics')).then((m) => ({
    default: m.OrganizationStatistics,
  })),
);

// Vues Team
const TeamDashboard = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamDashboard')).then((m) => ({default: m.TeamDashboard})),
);
const TeamPosts = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamPosts')).then((m) => ({default: m.TeamPosts})),
);
const TeamCalendar = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamCalendar')).then((m) => ({default: m.TeamCalendar})),
);
const TeamStatistics = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamStatistics')).then((m) => ({default: m.TeamStatistics})),
);
const TeamSettings = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamSettings/')).then((m) => ({default: m.TeamSettings})),
);
const TeamMonitoring = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamMonitoring')).then((m) => ({default: m.TeamMonitoring})),
);
const TeamStripeSuccess = lazy(() =>
  lazyWithRetry(() => import('./views/team/TeamStripeSuccess')).then((m) => ({default: m.TeamStripeSuccess})),
);

// Autres
const StatisticsSharingPage = lazy(() =>
  lazyWithRetry(() => import('./views/StatisticsSharingPage')).then((m) => ({default: m.StatisticsSharingPage})),
);
const StripeSuccess = lazy(() =>
  lazyWithRetry(() => import('./views/StripeSuccess')).then((m) => ({default: m.StripeSuccess})),
);
const PublicEmailSettings = lazy(() =>
  lazyWithRetry(() => import('./views/PublicEmailSettings')).then((m) => ({default: m.PublicEmailSettings})),
);
const PublicListPage = lazy(() =>
  lazyWithRetry(() => import('./views/PublicListPage')).then((m) => ({default: m.PublicListPage})),
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const logger = new ConsoleLogger('App');

// Le client React Query et son filet global : toute mutation refusée s'affiche en snackbar,
// sauf si l'écran s'en charge (cf. `createQueryClient`). `enqueueSnackbar` global de
// notistack et `i18n.t` : le client est créé hors React, sous aucun provider.
const queryClient = createQueryClient((error) =>
  showApiFailureSnackbar(enqueueSnackbar, i18n.t, error, {fallback: i18n.t('apiFailure.generic')}),
);

const LoadingFallback = () => (
  <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%'}}>
    <CircularProgress />
  </Box>
);

export function App() {
  useTrackPageView();

  useEffect(() => {
    // Connexion ou déconnexion : la réponse de `useAuthenticatedUserQuery` change, et avec
    // elle la porte de `GET /me` et `GET /me/premium-status`. Amplify n'émet ces deux
    // événements qu'APRÈS avoir écrit ou vidé ses jetons : `getCurrentUser()` relu ici dit
    // le nouvel état. Couvre aussi les connexions qui ne passent pas par `refreshSession`
    // (`reauthenticate`, `Hub` externe).
    const invalidateAuthenticated = () => {
      void queryClient.invalidateQueries({queryKey: sessionKeys.authenticated()});
    };
    const callback = Hub.listen('auth', ({payload}) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('user have been signedIn successfully.');
          invalidateAuthenticated();
          break;
        case 'signedOut':
          console.log('user have been signedOut successfully.');
          invalidateAuthenticated();
          break;
        case 'tokenRefresh':
          console.log('auth tokens have been refreshed.');
          break;
        // `tokenRefresh_failure` : traité par `useSessionExpiry` (sous `LoginRequiredRoute`),
        // seul écouteur de cet événement — il déconnecte et renvoie à `/signin`.
        case 'signInWithRedirect':
          console.log('signInWithRedirect API has successfully been resolved.');
          break;
        case 'signInWithRedirect_failure':
          console.log('failure while trying to resolve signInWithRedirect API.');
          break;
        case 'customOAuthState':
          logger.info('custom state returned from CognitoHosted UI');
          break;
      }
    });
    return () => {
      callback();
    };
  }, []);

  return (
    // ORDRE IMPOSÉ : `SnackbarProvider` est le PLUS INTERNE des providers dont ses
    // encarts dépendent. notistack ne rend pas les composants déclarés dans
    // `Components` parmi ses enfants, mais dans SON propre conteneur : ils ne voient
    // donc que les contextes situés AU-DESSUS de lui. `FreeTrial` lit le profil
    // (`useUser` → React Query) et l'essai (`useShouldShowTrialUI` → `useSession`),
    // `WorkingCommand` suit sa commande par React Query. `QueryClientProvider` et
    // `SessionProvider` doivent donc tous deux l'envelopper — n'en remonter qu'un
    // échange simplement une erreur contre l'autre.
    <QueryClientProvider client={queryClient}>
      {/* Statut premium et bêta : dérivés du profil et du jeton Cognito, avec
          une part d'état (cf. SessionProvider). */}
      <SessionProvider>
        <SnackbarProvider
          maxSnack={5}
          Components={{
            establishingConnection: EstablishingConnection,
            workingCommand: WorkingCommand,
            noLinkedInConnection: NoLinkedInConnection,
            linkedinSessionUnavailable: LinkedinSessionUnavailable,
            freeTrial: FreeTrial,
          }}
        >
          {/* Équipe et membres sélectionnés : de l'état d'interface, qui vivait
              dans `teamslice` faute d'un autre endroit partagé. Le provider lit
              `useTeamsQuery`, il doit donc être sous le QueryClientProvider. */}
          <TeamSelectionProvider>
            <CrispProvider>
              {/* On enveloppe tout dans Suspense pour gérer le chargement des chunks */}
              <Suspense fallback={<LoadingFallback />}>
                <SentryRoutes>
                  {/* Routes Publiques / Statiques */}
                  <Route path="/chat" element={<ExtensionChatKit />} />
                  <Route path="/drafts/:id/sharing" element={<DraftSharing />} />
                  <Route path="/public/email-settings" element={<PublicEmailSettings />} />

                  {/* Routes Lazy */}
                  <Route path="/statistics/sharing/:publicId" element={<StatisticsSharingPage />} />
                  <Route path="/list/:id/public" element={<PublicListPage />} />

                  <Route element={<Authentication />}>
                    <Route path="/confirm" element={<Confirm />} />
                    <Route path="/reset" element={<Reset />} />
                    <Route path="/signin" element={<Signin />} />
                    <Route path="/signup" element={<Signup />} />
                  </Route>

                  <Route element={<LoginRequiredRoute />}>
                    <Route path="auth/linkedin" element={<LinkedInAuth />} />

                    <Route element={<DashboardLayout />}>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/draft/new" element={<Dashboard />} />
                      <Route path="/draft/:draftId" element={<Dashboard />} />
                      <Route path="/posts" element={<Posts />} />
                      <Route path="/posts/published" element={<Posts />} />
                      {/* Page de détail d'un post publié. Tout l'état vient de l'URL : la page est
                      partageable et rechargeable. Le classement par spécificité de React Router
                      fait gagner le segment littéral /posts/published sur ce paramètre. */}
                      <Route path="/posts/:postId" element={<PostDetail />} />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/statistics" element={<Statistics />} />
                      <Route path="/watch" element={<Monitoring />} />
                      <Route path="/link" element={<Link />} />
                      <Route path="/subscription" element={<Navigate to="/settings/subscription" replace />} />
                      <Route path="auth/notion" element={<NotionCallback />} />
                      <Route path="auth/canva" element={<CanvaCallback />} />
                      <Route path="auth/google" element={<GoogleCalendarCallback />} />
                      <Route path="auth/microsoft" element={<MicrosoftCalendarCallback />} />

                      <Route path="organization/:id" element={<OrganizationWrapper />}>
                        <Route index element={<OrganizationDashboard />} />
                        <Route path="posts" element={<OrganizationPosts />} />
                        <Route path="posts/:postId" element={<PostDetail />} />
                        <Route path="calendar" element={<OrganizationCalendar />} />
                        <Route path="statistics" element={<OrganizationStatistics />} />
                      </Route>

                      <Route path="team/:id" element={<TeamWrapper />}>
                        <Route index element={<TeamDashboard />} />
                        <Route path="draft/new" element={<TeamDashboard />} />
                        <Route path="draft/:draftId" element={<TeamDashboard />} />
                        <Route path="posts" element={<TeamPosts />} />
                        <Route path="calendar" element={<TeamCalendar />} />
                        <Route path="statistics" element={<TeamStatistics />} />
                        <Route path="watch" element={<TeamMonitoring />} />
                        <Route path="settings" element={<TeamSettings />} />
                        <Route path="settings/privacy" element={<TeamSettings />} />
                        <Route path="settings/team" element={<TeamSettings />} />
                        <Route path="settings/organizations" element={<TeamSettings />} />
                        <Route path="settings/subscription" element={<TeamSettings />} />
                        <Route path="stripe-success" element={<TeamStripeSuccess />} />
                      </Route>

                      <Route path="referral" element={<Referral />} />
                      <Route path="settings/referrals" element={<Navigate to="/referral" replace />} />
                      <Route path="integration" element={<Integration />} />
                      <Route path="settings/integration" element={<Navigate to="/integration" replace />} />
                      <Route path="settings/subscription" element={<Settings />} />
                      {/* Lien des emails winback et de relance de paiement envoyés avant le
                      14/10/2026 : la query (`?promo=`) doit suivre. */}
                      <Route path="settings/billing" element={<NavigateKeepingSearch to="/settings/subscription" />} />
                      <Route path="settings/pages" element={<Settings />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="stripe-success" element={<StripeSuccess />} />
                    </Route>
                  </Route>
                </SentryRoutes>
              </Suspense>
            </CrispProvider>
          </TeamSelectionProvider>
        </SnackbarProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
